import api from './api';

export interface JurisprudenceItem {
  id: number;
  pandora_id: string;
  title: string;
  court_name: string;
  judging_body?: string;
  judgment_date?: string;
  ementa: string;
  legal_domain?: string;
  keywords?: string[];
  validation_status: string;
  validated_by?: string;
  quality_score?: number;
  imported_at?: string;
}

export interface JurisprudenceDetail extends JurisprudenceItem {
  cnj_number?: string;
  case_number?: string;
  binding_thesis?: string;
  main_text?: string;
  summary?: string;
  legal_category?: string;
  tags?: string[];
  validation_date?: string;
  validation_notes?: string;
  ai_confidence?: number;
  import_batch?: string;
  updated_at?: string;
}

export interface ImportLog {
  id: string;
  batch_id: string;
  import_type: string;
  status: string;
  total_records: number;
  imported_records: number;
  updated_records: number;
  failed_records: number;
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  success_rate?: number;
  error_log?: string;
}

export interface ImportResult {
  success: boolean;
  message: string;
  data: {
    status: string;
    batch_id: string;
    jurisprudences_imported: number;
    glossary_imported: number;
    embeddings_imported: number;
    total_processed: number;
    new_added: number;
    updated: number;
    failed: number;
  };
}

export interface Statistics {
  jurisprudence: {
    total: number;
    validated: number;
    pending: number;
    rejected: number;
    by_court: Record<string, number>;
    by_domain: Record<string, number>;
  };
  glossary: {
    total: number;
    validated: number;
  };
  recent_imports: Array<{
    batch_id: string;
    import_type: string;
    status: string;
    total_records: number;
    imported_records: number;
    started_at?: string;
  }>;
}

export interface GlossaryItem {
  id: number;
  title: string;
  definition: string;
  legal_domain?: string;
  validation_status: string;
  imported_at?: string;
}

/**
 * Importa arquivos do Pandora
 */
export const importPandoraData = async (
  coreDb: File,
  vectorsDb: File,
  importType: 'jurisprudence' | 'glossary' | 'both' = 'both'
): Promise<ImportResult> => {
  const formData = new FormData();
  formData.append('core_db', coreDb);
  formData.append('vectors_db', vectorsDb);
  
  // Usar fetch diretamente para evitar problemas com FormData
  const token = localStorage.getItem('token');
  const baseURL = import.meta.env.VITE_API_URL || '/api/v1';
  
  try {
    const response = await fetch(
      `${baseURL}/admin/jurisprudence/import?import_type=${importType}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      }
    );
    
    if (!response.ok) {
      let errorMessage = `Erro HTTP ${response.status}`;
      
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || errorMessage;
      } catch {
        const errorText = await response.text();
        if (errorText) {
          errorMessage = errorText;
        }
      }
      
      // Mensagens de erro mais amigáveis
      if (response.status === 403) {
        throw new Error('Acesso negado. Apenas administradores podem realizar importações.');
      } else if (response.status === 413) {
        throw new Error('Arquivos muito grandes. Tamanho máximo permitido: 50MB.');
      } else if (response.status === 504 || response.status === 502) {
        throw new Error('Tempo limite excedido. Tente novamente ou entre em contato com o suporte.');
      } else {
        throw new Error(`Erro ao fazer upload: ${errorMessage}`);
      }
    }
    
    return await response.json();
  } catch (error: any) {
    // Se for um erro de rede
    if (error.name === 'TypeError' || error.message.includes('Failed to fetch')) {
      throw new Error('Erro de conexão. Verifique sua internet e tente novamente.');
    }
    // Re-throw outros erros
    throw error;
  }
};

/**
 * Lista logs de importação
 */
export const getImportLogs = async (
  skip: number = 0,
  limit: number = 50,
  status?: string
): Promise<{ total: number; skip: number; limit: number; logs: ImportLog[] }> => {
  const params: any = { skip, limit };
  if (status) params.status = status;
  
  const response = await api.get('/admin/jurisprudence/import-logs', { params });
  return response.data;
};

/**
 * Lista jurisprudências com filtros
 */
export const listJurisprudence = async (
  skip: number = 0,
  limit: number = 50,
  filters?: {
    validation_status?: string;
    court_name?: string;
    legal_domain?: string;
    search?: string;
  }
): Promise<{ total: number; skip: number; limit: number; items: JurisprudenceItem[] }> => {
  const params: any = { skip, limit, ...filters };
  
  const response = await api.get('/admin/jurisprudence/jurisprudence', { params });
  return response.data;
};

/**
 * Obtém detalhes de uma jurisprudência
 */
export const getJurisprudenceDetail = async (id: number): Promise<JurisprudenceDetail> => {
  const response = await api.get(`/admin/jurisprudence/jurisprudence/${id}`);
  return response.data;
};

/**
 * Valida ou rejeita uma jurisprudência
 */
export const validateJurisprudence = async (
  id: number,
  validationStatus: 'validated' | 'rejected',
  notes?: string
): Promise<{ success: boolean; message: string; data: any }> => {
  const response = await api.patch(`/admin/jurisprudence/jurisprudence/${id}/validate`, {
    validation_status: validationStatus,
    notes,
  });
  return response.data;
};

/**
 * Desativa uma jurisprudência
 */
export const deleteJurisprudence = async (id: number): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`/admin/jurisprudence/jurisprudence/${id}`);
  return response.data;
};

/**
 * Obtém estatísticas
 */
export const getStatistics = async (): Promise<Statistics> => {
  const response = await api.get('/admin/jurisprudence/stats');
  return response.data;
};

/**
 * Lista glossário
 */
export const listGlossary = async (
  skip: number = 0,
  limit: number = 50,
  filters?: {
    legal_domain?: string;
    search?: string;
  }
): Promise<{ total: number; skip: number; limit: number; items: GlossaryItem[] }> => {
  const params: any = { skip, limit, ...filters };
  
  const response = await api.get('/admin/jurisprudence/glossary', { params });
  return response.data;
};

